<div class="ops-page">

  <!-- ─── Left rail ──────────────────────────────────────────── -->
  <aside class="ops-rail">

    <!-- Machine function filter -->
    <mat-card class="ops-card">
      <mat-card-header>
        <mat-card-title class="ops-card-title">
          <mat-icon class="ops-card-icon">layers</mat-icon>
          Machine operation
        </mat-card-title>
      </mat-card-header>
      <mat-card-content class="ops-card-content">
        <button class="ops-fn-btn"
                [class.is-active]="selectedMachineFunction === null"
                (click)="onMachineFunctionSelect(null)">
          <span class="ops-fn-bullet" [class.is-active]="selectedMachineFunction === null"></span>
          <span class="ops-fn-label">All operations</span>
        </button>
        <button *ngFor="let fn of machineFunctions"
                class="ops-fn-btn"
                [class.is-active]="selectedMachineFunction === fn"
                (click)="onMachineFunctionSelect(fn)">
          <span class="ops-fn-bullet" [class.is-active]="selectedMachineFunction === fn"></span>
          <span class="ops-fn-label">{{ functonLabel[fn] || fn }}</span>
        </button>
      </mat-card-content>
    </mat-card>

    <!-- Machine filter -->
    <mat-card class="ops-card" *ngIf="filteredMachines.length">
      <mat-card-header>
        <mat-card-title class="ops-card-title">
          <mat-icon class="ops-card-icon">memory</mat-icon>
          Machine
        </mat-card-title>
      </mat-card-header>
      <mat-card-content class="ops-card-content">
        <button class="ops-machine-pill"
                [class.is-active]="selectedMachineId === null"
                (click)="onMachineSelect(null)">
          <span class="ops-pill-dot ops-pill-dot--all"></span>
          <div class="ops-pill-text">
            <div class="ops-pill-name">All machines</div>
            <div class="ops-pill-sub">Aggregate across the operation</div>
          </div>
        </button>
        <button *ngFor="let m of filteredMachines"
                class="ops-machine-pill"
                [class.is-active]="selectedMachineId === m.id"
                (click)="onMachineSelect(m.id)">
          <span class="ops-pill-dot"></span>
          <div class="ops-pill-text">
            <div class="ops-pill-name">{{ m.name }}</div>
            <div class="ops-pill-sub">SN {{ m.serialNumber }}</div>
          </div>
        </button>
      </mat-card-content>
    </mat-card>

    <!-- Legend -->
    <mat-card class="ops-card">
      <mat-card-content style="padding: 14px 16px;">
        <div class="ops-legend-title">Legend</div>
        <div class="ops-legend-row">
          <span class="ops-legend-swatch ops-legend-swatch--time"></span>
          <span class="ops-legend-label">Machine working time</span>
          <span class="ops-legend-axis">(left)</span>
        </div>
        <div class="ops-legend-row">
          <span class="ops-legend-swatch ops-legend-swatch--jobs"></span>
          <span class="ops-legend-label"># jobs</span>
          <span class="ops-legend-axis">(right)</span>
        </div>
      </mat-card-content>
    </mat-card>

  </aside>

  <!-- ─── Main area ──────────────────────────────────────────── -->
  <div class="ops-main">

    <!-- Header bar -->
    <div class="ops-header">
      <div class="ops-header-meta">
        <h2 class="ops-title">Machine activity by operator</h2>
        <span class="ops-subtitle">
          {{ selectedMachineFunction ? functonLabel[selectedMachineFunction] : 'All operations' }}
          · {{ periodLabel }} · {{ periodMeta }}
        </span>
      </div>
      <div class="ops-header-controls">
        <div class="ops-segmented">
          <button [class.is-active]="selectedPeriod === 'DAILY'" (click)="onPeriodChange('DAILY')">Daily</button>
          <button [class.is-active]="selectedPeriod === 'WEEKLY'" (click)="onPeriodChange('WEEKLY')">Weekly</button>
          <button [class.is-active]="selectedPeriod === 'MONTHLY'" (click)="onPeriodChange('MONTHLY')">Monthly</button>
        </div>
        <mat-select class="ops-sort-select" [(ngModel)]="sortBy" [ngModelOptions]="{standalone: true}">
          <mat-option value="time">Sort: by activity</mat-option>
          <mat-option value="name">Sort: by name</mat-option>
        </mat-select>
        <button mat-stroked-button class="ops-csv-btn"
                (click)="downloadCsv()"
                [disabled]="isLoading || operatorRows.length === 0"
                matTooltip="Download data as CSV">
          <mat-icon>download</mat-icon>
          CSV
        </button>
      </div>
    </div>

    <!-- Disclaimer banner -->
    <mat-card class="ops-banner" *ngIf="showBanner">
      <div class="ops-banner-icon">
        <mat-icon>info_outline</mat-icon>
      </div>
      <div class="ops-banner-body">
        <div class="ops-banner-headline">
          This is <span class="ops-banner-accent">machine activity</span>, not operator attendance.
        </div>
        <div class="ops-banner-text">
          Operator clock-in / clock-out events are not currently collected. These charts measure
          how much the machines associated with each operator have worked in the selected period
          — not how many hours the operator was on the floor. Use this as an indirect indicator
          and avoid framing it as a productivity score.
        </div>
      </div>
      <button mat-icon-button class="ops-banner-close" (click)="showBanner = false" title="Dismiss">
        <mat-icon>close</mat-icon>
      </button>
    </mat-card>

    <!-- Loading indicator -->
    <div class="ops-loading" *ngIf="isLoading">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    </div>

    <!-- Empty state -->
    <mat-card class="ops-empty" *ngIf="!isLoading && operatorRows.length === 0">
      <mat-card-content>
        <mat-icon class="ops-empty-icon">people_outline</mat-icon>
        <p>No operator data available for the selected period and filters.</p>
      </mat-card-content>
    </mat-card>

    <ng-container *ngIf="!isLoading && operatorRows.length > 0">

      <!-- Ranking strip -->
      <mat-card class="ops-card">
        <mat-card-header>
          <mat-card-title class="ops-card-title">
            <mat-icon class="ops-card-icon">trending_up</mat-icon>
            Ranking · total {{ periodLabel.toLowerCase() }} machine time
            <span class="ops-card-subtitle">over the selected periods</span>
          </mat-card-title>
        </mat-card-header>
        <mat-card-content class="ops-card-content">
          <div *ngFor="let row of sortedRows; let i = index" class="ops-rank-row">
            <div class="ops-rank-badge" [class.ops-rank-badge--first]="i === 0"
                 [style.background]="i === 0 ? palette(row).hex : null">#{{ i + 1 }}</div>
            <div class="ops-rank-identity">
              <div class="ops-rank-name">
                <span class="ops-op-color-dot" [style.background]="palette(row).hex"></span>
                {{ row.operatorName }}
              </div>
              <div class="ops-rank-code">{{ row.operatorCode }}</div>
            </div>
            <div class="ops-rank-bar-wrap">
              <div class="ops-rank-bar-fill"
                   [style.background]="palette(row).bg"
                   [style.width.%]="(row.totalWorkingTimeSecs / (sortedRows[0].totalWorkingTimeSecs || 1)) * 100">
              </div>
              <span class="ops-rank-bar-jobs">{{ row.totalJobs }} jobs</span>
            </div>
            <div class="ops-rank-time" [style.color]="palette(row).hex">{{ fmtWorkingTime(row.totalWorkingTimeSecs) }}</div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Per-operator charts grid -->
      <div class="ops-charts-grid" [class.ops-charts-grid--wide]="sortedRows.length > 4">
        <mat-card class="ops-card ops-operator-card" *ngFor="let row of sortedRows; let i = index">
          <mat-card-header>
            <mat-card-title class="ops-card-title">
              <span class="ops-op-rank" [class.ops-op-rank--first]="i === 0"
                    [style.background]="i === 0 ? palette(row).hex : null">#{{ i + 1 }}</span>
              <div class="ops-op-identity">
                <span class="ops-op-name">
                  <span class="ops-op-color-dot" [style.background]="palette(row).hex"></span>
                  {{ row.operatorName }}
                </span>
                <span class="ops-op-code">{{ row.operatorCode }}</span>
              </div>
              <div class="ops-kpi-cluster">
                <div class="ops-kpi">
                  <div class="ops-kpi-label">Total time</div>
                  <div class="ops-kpi-value ops-kpi-value--time">{{ fmtWorkingTime(row.totalWorkingTimeSecs) }}</div>
                </div>
                <div class="ops-kpi">
                  <div class="ops-kpi-label">Total jobs</div>
                  <div class="ops-kpi-value ops-kpi-value--jobs">{{ row.totalJobs }}</div>
                </div>
              </div>
            </mat-card-title>
          </mat-card-header>
          <mat-card-content class="ops-chart-wrap">
            <div class="ops-chart-container">
              <canvas baseChart
                      [datasets]="row.chartDatasets"
                      [labels]="row.chartLabels"
                      [options]="chartOptions"
                      [chartType]="chartType"
                      [legend]="false">
              </canvas>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

    </ng-container>

  </div>

</div>
